
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import rules from "@/store/rules";
import { CustomerArticleDto } from "@/dtos/customerArticleDto";
import { WerkstoffDto } from "@/dtos/werkstoffDto";
import { ArticleDataDto } from "@/dtos/articleDataDto";
import { MaterialDto } from "@/dtos/materialDto";
import ImportArticle from "./ImportArticle.vue";
import { PressJoinParameterDto } from "@/dtos/pressJoinParameterDto";
import { PressJoinCategory } from "@/dtos/pressJoinCategory";

@Component({
	components: {
		ImportArticle,
	},
})
export default class ImportAssembly extends Vue {
	@Prop()
	private articleId!: number;
	private articleName: string = "";
	private articleNumber: string = "";
	private deleting: boolean = false;
	private articleDataValid: boolean = true;
	private requiredStringMax50Rule: any = rules.requiredStringMax50Rule;
	private optionalStringMax50Rule: any = rules.optionalStringMax50Rule;
	private selectedWerkstoffId: number = 0;

	private mounted() {
		this.articleName = this.article.customerArticleName;
	}

	private get article(): CustomerArticleDto {
		return this.$store.state.importingArticles.find(
			(a: CustomerArticleDto) => a.articleId == this.articleId
		);
	}

	private get subArticles(): CustomerArticleDto[] {
		return this.$store.state.importingArticles.filter(
			(a: CustomerArticleDto) => a.parentArticleId == this.articleId
		);
	}

	private get subArticleWerkstoffThicknesses(): { werkstoffId: number; thickness: number }[] {
		return this.subArticles.map((a: CustomerArticleDto) => ({
			werkstoffId: a.werkstoffId,
			thickness: a.thickness,
		}));
	}

	private get availablePressJoins() {
		const allPressJoins = this.$store.state.pressJoins as PressJoinParameterDto[];

		// If no werkstoff or thickness is set for any subarticle, show all press joins
		if (
			this.subArticleWerkstoffThicknesses.every(
				(s: any) => !s.werkstoffId || s.werkstoffId <= 0
			) &&
			this.subArticleWerkstoffThicknesses.every((s: any) => s.thickness <= 0)
		) {
			return allPressJoins.map((p) => ({
				text: p.groupId,
				groupId: p.groupId,
				category: p.category,
			}));
		}

		let filtered = allPressJoins;

		// If some subarticles have a werkstoff selected,
		// filter press joins that are available for these werkstoffe
		if (
			this.subArticleWerkstoffThicknesses.some((s: any) => s.werkstoffId && s.werkstoffId > 0)
		) {
			filtered = [];
			const werkstoffIds = this.subArticleWerkstoffThicknesses.map((s: any) => s.werkstoffId);
			const filteredWerkstoffe = (this.$store.state.werkstoffe as WerkstoffDto[]).filter(
				(w: WerkstoffDto) => werkstoffIds.includes(w.blexonId)
			);
			// get all the press joins of the filtered werkstoffe
			const werkstoffPressJoinIds = filteredWerkstoffe.flatMap((w: WerkstoffDto) =>
				w.materials.flatMap((m: MaterialDto) => m.pressJoinToolIds)
			);
			const distinctIds = new Set(werkstoffPressJoinIds);
			filtered = allPressJoins.filter((p: PressJoinParameterDto) =>
				distinctIds.has(p.werkzeugId)
			);
		}

		// If some subarticles have a thickness,
		// filter press joins that are available for these thicknesses
		if (this.subArticleWerkstoffThicknesses.some((s: any) => s.thickness > 0)) {
			const thicknesses = this.subArticleWerkstoffThicknesses.map((s: any) => s.thickness);
			const filteredMaterials = (this.$store.state.werkstoffe as WerkstoffDto[])
				.flatMap((w: WerkstoffDto) => w.materials)
				.filter((m: MaterialDto) => thicknesses.includes(m.thickness));
			const filteredMaterialPressJoinIds = filteredMaterials.flatMap(
				(m: MaterialDto) => m.pressJoinToolIds
			);
			const distinctIds = new Set(filteredMaterialPressJoinIds);
			// get all the press joins of the filtered materials
			const thicknessFiltered = allPressJoins.filter((p: PressJoinParameterDto) =>
				distinctIds.has(p.werkzeugId)
			);
			// get intersection of previous filtered array and the thickness filtered one
			filtered = [...new Map(filtered.map((x) => [x.werkzeugId, x]))]
				.filter(([id]) => thicknessFiltered.some((y) => y.werkzeugId === id))
				.map(([, value]) => value);
		}

		const filteredMap = filtered.map((p) => ({
			text: p.groupName,
			groupId: p.groupId,
			category: p.category,
		}));

		const distinct = filteredMap.filter((obj, index, self) =>
			index === self.findIndex((t) => t.groupId === obj.groupId)
		);
		return distinct;
	}

	private get subArticlesValid(): boolean {
		return this.subArticles.every(
			(a) => a.werkstoffId > 0 || (a.pressJoinGroupId != null && (a.pressJoinGroupId > 0 || a.pressJoinGroupId === -99))
		);
	}

	@Watch("selectedWerkstoffId")
	private selectedWerkstoffIdChanged() {
		if (this.selectedWerkstoffId > 0) {
			const selectedWerkstoff = (this.$store.state.werkstoffe as WerkstoffDto[]).find(
				(w) => w.blexonId === this.selectedWerkstoffId
			);
			for (const article of this.subArticles) {
				if (
					selectedWerkstoff &&
					selectedWerkstoff.materials.some((m) => article.thickness === m.thickness)
				) {
					article.werkstoffId = this.selectedWerkstoffId;
				}
			}
		}
	}

	/**
	 * Gets the raw materials that are available for ALL subarticles
	 */
	private get selectableWerkstoffe() {
		const allThicknesses = this.subArticles.flatMap((a) => a.thickness);
		var availableWerkstoffe: WerkstoffDto[] = (
			this.$store.state.werkstoffe as WerkstoffDto[]
		).filter((w) => w.materials.some((m: MaterialDto) => allThicknesses.includes(m.thickness)));
		return availableWerkstoffe;
	}

	@Watch("isCalculating")
	private resetCalculatingMessage() {
		if (!this.isCalculating) {
			this.article.calculatingMessage = "";
		}
	}

	@Watch("article.customerArticleName")
	private updateArticleName() {
		this.articleName = this.article.customerArticleName;
	}

	private get isCalculating() {
		return this.article.calculating;
	}

	private get articleInitialized() {
		return this.article.articleId >= 0;
	}

	private async blexonisieren() {
		// First all press joins
		for (const article of this.subArticles.filter(
			(a) => a.pressJoinGroupId != null && a.pressJoinGroupId > 0 && a.pressJoinGroupId !== -99
		)){
			this.$store.dispatch("blexonisieren", { article: article, validate: true });
		}
		// Then all normal sheet metal
		for (const article of this.subArticles.filter(
			(a) => !a.pressJoinGroupId || (a.pressJoinGroupId <= 0 && a.pressJoinGroupId !== -99)
		)) {
			this.$store.dispatch("blexonisieren", { article: article, validate: true });
		}

		// The assembly needs a raw material to disappear from the importing articles
		// but it does not matter what raw material it is.
		var werkstoffId = 3; // This is AlMg3
		this.article.werkstoffId = werkstoffId;
		await this.$store.dispatch("blexonisieren", { article: this.article, validate: true });

		// Deleted parts
		for (const article of this.subArticles.filter(
			(a) => a.pressJoinGroupId != null && a.pressJoinGroupId === -99
		)){
			this.$store.dispatch("blexonisieren", { article: article, validate: true });
		}
		
	}

	private getThumbnailUrl(articleId: number) {
		return this.$store.getters.getThumbnailUrl(articleId, undefined);
	}

	private async deleteImportingArticle() {
		try {
			this.deleting = true;
			const response = await httpClient().post("article/deleteArticles", [
				this.article.articleId,
			]);
			Vue.nextTick(() => {
				console.log(`Artikel ${this.article.articleId} wurde gelöscht`);
				this.$store.commit("deleteArticle", this.article.articleId);
			});
		} catch (err) {
			console.log(`Fehler beim Löschen des Artikels. ${err}`);
			this.$store.dispatch("setSnackbarErrorText", "Artikel konnte nicht gelöscht werden.");
		} finally {
			this.deleting = false;
		}
	}

	private async saveArticleData() {
		if (
			!this.articleDataValid ||
			(this.article.customerArticleName === this.articleName &&
				this.article.customerArticleId === this.articleNumber)
		) {
			return;
		}
		let articleData: ArticleDataDto = {
			articleId: this.article.articleId,
			customerArticleId: this.articleNumber,
			customerArticleName: this.articleName,
		};
		try {
			await httpClient().post("article/UpdateArticleData", articleData);
			this.$store.commit("saveArticleData", articleData);
		} catch (err) {
			console.log(
				`Failed to save article data of article ${this.article.articleId}. Error: ${err}`
			);
		}
	}
}
